/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import { PartnerRegistrations } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import { EventGridManagementClient } from "../eventGridManagementClient.js";
import {
  SimplePollerLike,
  OperationState,
  createHttpPoller,
} from "@azure/core-lro";
import { createLroSpec } from "../lroImpl.js";
import {
  PartnerRegistration,
  PartnerRegistrationsListBySubscriptionNextOptionalParams,
  PartnerRegistrationsListBySubscriptionOptionalParams,
  PartnerRegistrationsListBySubscriptionResponse,
  PartnerRegistrationsListByResourceGroupNextOptionalParams,
  PartnerRegistrationsListByResourceGroupOptionalParams,
  PartnerRegistrationsListByResourceGroupResponse,
  PartnerRegistrationsGetOptionalParams,
  PartnerRegistrationsGetResponse,
  PartnerRegistrationsCreateOrUpdateOptionalParams,
  PartnerRegistrationsCreateOrUpdateResponse,
  PartnerRegistrationsDeleteOptionalParams,
  PartnerRegistrationUpdateParameters,
  PartnerRegistrationsUpdateOptionalParams,
  PartnerRegistrationsListBySubscriptionNextResponse,
  PartnerRegistrationsListByResourceGroupNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing PartnerRegistrations operations. */
export class PartnerRegistrationsImpl implements PartnerRegistrations {
  private readonly client: EventGridManagementClient;

  /**
   * Initialize a new instance of the class PartnerRegistrations class.
   * @param client Reference to the service client
   */
  constructor(client: EventGridManagementClient) {
    this.client = client;
  }

  /**
   * List all the partner registrations under an Azure subscription.
   * @param options The options parameters.
   */
  public listBySubscription(
    options?: PartnerRegistrationsListBySubscriptionOptionalParams,
  ): PagedAsyncIterableIterator<PartnerRegistration> {
    const iter = this.listBySubscriptionPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listBySubscriptionPagingPage(options, settings);
      },
    };
  }

  private async *listBySubscriptionPagingPage(
    options?: PartnerRegistrationsListBySubscriptionOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<PartnerRegistration[]> {
    let result: PartnerRegistrationsListBySubscriptionResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listBySubscription(options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listBySubscriptionNext(continuationToken, options);
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listBySubscriptionPagingAll(
    options?: PartnerRegistrationsListBySubscriptionOptionalParams,
  ): AsyncIterableIterator<PartnerRegistration> {
    for await (const page of this.listBySubscriptionPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * List all the partner registrations under a resource group.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param options The options parameters.
   */
  public listByResourceGroup(
    resourceGroupName: string,
    options?: PartnerRegistrationsListByResourceGroupOptionalParams,
  ): PagedAsyncIterableIterator<PartnerRegistration> {
    const iter = this.listByResourceGroupPagingAll(resourceGroupName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listByResourceGroupPagingPage(
          resourceGroupName,
          options,
          settings,
        );
      },
    };
  }

  private async *listByResourceGroupPagingPage(
    resourceGroupName: string,
    options?: PartnerRegistrationsListByResourceGroupOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<PartnerRegistration[]> {
    let result: PartnerRegistrationsListByResourceGroupResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listByResourceGroup(resourceGroupName, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listByResourceGroupNext(
        resourceGroupName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listByResourceGroupPagingAll(
    resourceGroupName: string,
    options?: PartnerRegistrationsListByResourceGroupOptionalParams,
  ): AsyncIterableIterator<PartnerRegistration> {
    for await (const page of this.listByResourceGroupPagingPage(
      resourceGroupName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Gets a partner registration with the specified parameters.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param partnerRegistrationName Name of the partner registration.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    partnerRegistrationName: string,
    options?: PartnerRegistrationsGetOptionalParams,
  ): Promise<PartnerRegistrationsGetResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, partnerRegistrationName, options },
      getOperationSpec,
    );
  }

  /**
   * Creates a new partner registration with the specified parameters.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param partnerRegistrationName Name of the partner registration.
   * @param partnerRegistrationInfo PartnerRegistration information.
   * @param options The options parameters.
   */
  async beginCreateOrUpdate(
    resourceGroupName: string,
    partnerRegistrationName: string,
    partnerRegistrationInfo: PartnerRegistration,
    options?: PartnerRegistrationsCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<PartnerRegistrationsCreateOrUpdateResponse>,
      PartnerRegistrationsCreateOrUpdateResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<PartnerRegistrationsCreateOrUpdateResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined =
        undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        partnerRegistrationName,
        partnerRegistrationInfo,
        options,
      },
      spec: createOrUpdateOperationSpec,
    });
    const poller = await createHttpPoller<
      PartnerRegistrationsCreateOrUpdateResponse,
      OperationState<PartnerRegistrationsCreateOrUpdateResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
    });
    await poller.poll();
    return poller;
  }

  /**
   * Creates a new partner registration with the specified parameters.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param partnerRegistrationName Name of the partner registration.
   * @param partnerRegistrationInfo PartnerRegistration information.
   * @param options The options parameters.
   */
  async beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    partnerRegistrationName: string,
    partnerRegistrationInfo: PartnerRegistration,
    options?: PartnerRegistrationsCreateOrUpdateOptionalParams,
  ): Promise<PartnerRegistrationsCreateOrUpdateResponse> {
    const poller = await this.beginCreateOrUpdate(
      resourceGroupName,
      partnerRegistrationName,
      partnerRegistrationInfo,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Deletes a partner registration with the specified parameters.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param partnerRegistrationName Name of the partner registration.
   * @param options The options parameters.
   */
  async beginDelete(
    resourceGroupName: string,
    partnerRegistrationName: string,
    options?: PartnerRegistrationsDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>> {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<void> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined =
        undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, partnerRegistrationName, options },
      spec: deleteOperationSpec,
    });
    const poller = await createHttpPoller<void, OperationState<void>>(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
    });
    await poller.poll();
    return poller;
  }

  /**
   * Deletes a partner registration with the specified parameters.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param partnerRegistrationName Name of the partner registration.
   * @param options The options parameters.
   */
  async beginDeleteAndWait(
    resourceGroupName: string,
    partnerRegistrationName: string,
    options?: PartnerRegistrationsDeleteOptionalParams,
  ): Promise<void> {
    const poller = await this.beginDelete(
      resourceGroupName,
      partnerRegistrationName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Updates a partner registration with the specified parameters.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param partnerRegistrationName Name of the partner registration.
   * @param partnerRegistrationUpdateParameters Partner registration update information.
   * @param options The options parameters.
   */
  async beginUpdate(
    resourceGroupName: string,
    partnerRegistrationName: string,
    partnerRegistrationUpdateParameters: PartnerRegistrationUpdateParameters,
    options?: PartnerRegistrationsUpdateOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>> {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<void> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined =
        undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        partnerRegistrationName,
        partnerRegistrationUpdateParameters,
        options,
      },
      spec: updateOperationSpec,
    });
    const poller = await createHttpPoller<void, OperationState<void>>(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
    });
    await poller.poll();
    return poller;
  }

  /**
   * Updates a partner registration with the specified parameters.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param partnerRegistrationName Name of the partner registration.
   * @param partnerRegistrationUpdateParameters Partner registration update information.
   * @param options The options parameters.
   */
  async beginUpdateAndWait(
    resourceGroupName: string,
    partnerRegistrationName: string,
    partnerRegistrationUpdateParameters: PartnerRegistrationUpdateParameters,
    options?: PartnerRegistrationsUpdateOptionalParams,
  ): Promise<void> {
    const poller = await this.beginUpdate(
      resourceGroupName,
      partnerRegistrationName,
      partnerRegistrationUpdateParameters,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * List all the partner registrations under an Azure subscription.
   * @param options The options parameters.
   */
  private _listBySubscription(
    options?: PartnerRegistrationsListBySubscriptionOptionalParams,
  ): Promise<PartnerRegistrationsListBySubscriptionResponse> {
    return this.client.sendOperationRequest(
      { options },
      listBySubscriptionOperationSpec,
    );
  }

  /**
   * List all the partner registrations under a resource group.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param options The options parameters.
   */
  private _listByResourceGroup(
    resourceGroupName: string,
    options?: PartnerRegistrationsListByResourceGroupOptionalParams,
  ): Promise<PartnerRegistrationsListByResourceGroupResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, options },
      listByResourceGroupOperationSpec,
    );
  }

  /**
   * ListBySubscriptionNext
   * @param nextLink The nextLink from the previous successful call to the ListBySubscription method.
   * @param options The options parameters.
   */
  private _listBySubscriptionNext(
    nextLink: string,
    options?: PartnerRegistrationsListBySubscriptionNextOptionalParams,
  ): Promise<PartnerRegistrationsListBySubscriptionNextResponse> {
    return this.client.sendOperationRequest(
      { nextLink, options },
      listBySubscriptionNextOperationSpec,
    );
  }

  /**
   * ListByResourceGroupNext
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param nextLink The nextLink from the previous successful call to the ListByResourceGroup method.
   * @param options The options parameters.
   */
  private _listByResourceGroupNext(
    resourceGroupName: string,
    nextLink: string,
    options?: PartnerRegistrationsListByResourceGroupNextOptionalParams,
  ): Promise<PartnerRegistrationsListByResourceGroupNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, nextLink, options },
      listByResourceGroupNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.EventGrid/partnerRegistrations/{partnerRegistrationName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PartnerRegistration,
    },
    default: {},
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.partnerRegistrationName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createOrUpdateOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.EventGrid/partnerRegistrations/{partnerRegistrationName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.PartnerRegistration,
    },
    201: {
      bodyMapper: Mappers.PartnerRegistration,
    },
    202: {
      bodyMapper: Mappers.PartnerRegistration,
    },
    204: {
      bodyMapper: Mappers.PartnerRegistration,
    },
    default: {},
  },
  requestBody: Parameters.partnerRegistrationInfo,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.partnerRegistrationName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const deleteOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.EventGrid/partnerRegistrations/{partnerRegistrationName}",
  httpMethod: "DELETE",
  responses: { 200: {}, 201: {}, 202: {}, 204: {}, default: {} },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.partnerRegistrationName,
  ],
  serializer,
};
const updateOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.EventGrid/partnerRegistrations/{partnerRegistrationName}",
  httpMethod: "PATCH",
  responses: { 200: {}, 201: {}, 202: {}, 204: {}, default: {} },
  requestBody: Parameters.partnerRegistrationUpdateParameters,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.partnerRegistrationName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const listBySubscriptionOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.EventGrid/partnerRegistrations",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PartnerRegistrationsListResult,
    },
    default: {},
  },
  queryParameters: [Parameters.apiVersion, Parameters.filter, Parameters.top],
  urlParameters: [Parameters.$host, Parameters.subscriptionId],
  headerParameters: [Parameters.accept],
  serializer,
};
const listByResourceGroupOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.EventGrid/partnerRegistrations",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PartnerRegistrationsListResult,
    },
    default: {},
  },
  queryParameters: [Parameters.apiVersion, Parameters.filter, Parameters.top],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listBySubscriptionNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PartnerRegistrationsListResult,
    },
    default: {},
  },
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listByResourceGroupNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PartnerRegistrationsListResult,
    },
    default: {},
  },
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
